import { StatsigClient } from "@statsig/js-client";
import { compact, flatMap } from "lodash/fp";

import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type { IntegrationAccessSetting } from "@app/common/types";
import type {
  IntegrationProviderCategory,
  IntegrationProviderId,
} from "@app/common/types/providerTypes";
import Config from "@app/config";

export interface IntegrationSettingCategorySetting {
  provider: IntegrationProviderId;
  settings: IntegrationAccessSetting[];
  disabled?: () => boolean;
}

export interface IntegrationSettingCategory {
  id: IntegrationProviderCategory;
  title: string;
  settings: IntegrationSettingCategorySetting[];
}

export const WORKSPACE_SETTING_TO_PROVIDER_ID: {
  [key in IntegrationAccessSetting]: IntegrationProviderId;
} = {
  integrationGoogleCalendarEnabled: "google_calendar",
  integrationGoogleEmailEnabled: "google_email",
  integrationHubspotPullEnabled: "hubspot",
  integrationHubspotPushEnabled: "hubspot",
  integrationMicrosoftGraphCalendarEnabled: "microsoft_graph_calendar",
  integrationMicrosoftGraphTeamsChatIngestEnabled:
    "microsoft_graph_teams_chat_ingest",
  integrationMicrosoftGraphEmailEnabled: "microsoft_graph_email",
  integrationMicrosoftGraphTeamsPushEnabled: "microsoft_graph_teams_push",
  integrationSalesforcePullEnabled: "salesforce",
  integrationSalesforcePushEnabled: "salesforce",
  integrationSlackBotEnabled: "slack_bot",
  integrationSlackIngestEnabled: "slack_ingest",
  integrationWebhooksEnabled: "webhooks",
  integrationZapierEnabled: "zapier",
  integrationZoomCalendarEnabled: "zoom_calendar",
};

export const INTEGRATION_CATEGORIES: IntegrationSettingCategory[] = [
  {
    id: "calendar",
    title: "Calendar",
    settings: [
      {
        provider: "google_calendar",
        settings: ["integrationGoogleCalendarEnabled"],
      },
      {
        provider: "microsoft_graph_calendar",
        settings: ["integrationMicrosoftGraphCalendarEnabled"],
      },
      {
        provider: "zoom_calendar",
        settings: ["integrationZoomCalendarEnabled"],
      },
    ],
  },
  {
    id: "productivity",
    title: "Productivity",
    settings: [
      {
        provider: "google_email",
        settings: ["integrationGoogleEmailEnabled"],
      },
      {
        provider: "microsoft_graph_email",
        settings: ["integrationMicrosoftGraphEmailEnabled"],
      },
      {
        provider: "slack_ingest",
        settings: ["integrationSlackIngestEnabled"],
      },
      {
        provider: "microsoft_graph_teams_chat_ingest",
        settings: ["integrationMicrosoftGraphTeamsChatIngestEnabled"],
      },
    ],
  },
  {
    id: "messaging",
    title: "Messaging",
    settings: [
      {
        provider: "slack_bot",
        settings: ["integrationSlackBotEnabled"],
      },
      {
        provider: "microsoft_graph_teams_push",
        settings: ["integrationMicrosoftGraphTeamsPushEnabled"],
      },
    ],
  },
  {
    id: "crm",
    title: "CRM",
    settings: compact([
      {
        provider: "hubspot",
        settings: [
          "integrationHubspotPullEnabled",
          "integrationHubspotPushEnabled",
        ],
      },
      {
        disabled: () =>
          !(
            Config.flags.enableSalesforceIntegration ||
            StatsigClient.instance().checkGate("enable_salesforce")
          ),
        provider: "salesforce",
        settings: [
          "integrationSalesforcePullEnabled",
          "integrationSalesforcePushEnabled",
        ],
      },
    ]),
  },
  {
    id: "workflow",
    title: "Workflow",
    settings: [
      { provider: "webhooks", settings: ["integrationWebhooksEnabled"] },
      { provider: "zapier", settings: ["integrationZapierEnabled"] },
    ],
  },
];

export const ALL_INTEGRATION_SETTINGS: IntegrationAccessSetting[] = flatMap(
  "settings",
  flatMap("settings", INTEGRATION_CATEGORIES)
);

export const INTEGRATION_DETAIL_BY_PROVIDER_ID: {
  [key in IntegrationProviderId]: string;
} = {
  // calendars
  google_calendar: `Read Assistant can join and generate meeting reports for ${INTEGRATION_PROVIDERS_BY_ID.google_calendar.displayName} events`,
  microsoft_graph_calendar: `Read Assistant can join and generate meeting reports for ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_calendar.displayName} events`,
  zoom_calendar: `Read Assistant can join and generate meeting reports for ${INTEGRATION_PROVIDERS_BY_ID.zoom_calendar.displayName} events`,

  // productivity
  google_email: `Topic summary reports from a ${INTEGRATION_PROVIDERS_BY_ID.google_email.displayNameShort} inbox`,
  microsoft_graph_email: `Topic summary reports from a ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_email.displayNameShort} inbox`,
  microsoft_graph_teams_chat_ingest: `Topic summary reports from ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_chat_ingest.displayNameShort} messages and channels`,
  slack_ingest: `Topic summary reports from ${INTEGRATION_PROVIDERS_BY_ID.slack_ingest.displayNameShort} channels`,

  // messaging
  microsoft_graph_teams_push: `Share and receive meeting summaries through ${INTEGRATION_PROVIDERS_BY_ID.microsoft_graph_teams_push.displayNameShort}`,
  slack_bot: `Share and receive meeting summaries through ${INTEGRATION_PROVIDERS_BY_ID.slack_bot.displayNameShort}`,

  // workflow
  webhooks: "Create custom automated workflows with webhooks",
  zapier: `Push summaries into other apps using a ${INTEGRATION_PROVIDERS_BY_ID.zapier.displayName} integration`,

  // crm
  hubspot: `Sync contact and meeting note data between Read and ${INTEGRATION_PROVIDERS_BY_ID.hubspot.displayName}`,
  salesforce: `Sync contact and meeting note data between Read and ${INTEGRATION_PROVIDERS_BY_ID.salesforce.displayName}`,
};
